import React from 'react'
import { useIntl } from 'react-intl'
import { Column } from 'primereact/column'
import useModal from '../../utils/hooks/useModal'
import MutualsService from '../../services/mutuals/mutuals.service'
import Modal from '../../components/modal/Modal'
import MutualForm from '../../components/mutual/MutualForm'
import { DataTableFun } from '../../utils/helpers/dataTableFun'
import useAccess from '../../utils/hooks/useAccess'
import { GROUPS, MODULES } from '../../utils/constants/roles'
import useInputs from '../../utils/hooks/useInputs'
import DataTableComp from '../../components/common/data-table/DataTableComp'
import useHandlerBasicActions from '../../utils/hooks/useHandlerBasicActions'
import EditButton from '../../components/common/buttons/EditButton'
import MainCommonView from '../../components/common/MainCommonView'

const Mutuals = () => {
  const { formatMessage: msg } = useIntl()

  const { isShowing, toggle } = useModal()
  const { isShowing: isShowingEdit, toggle: toggleEdit } = useModal()
  const { canAdd, canDelete, canEdit } = useAccess()
  const { inputs, updateInputs } = useInputs()

  const dataFacade = useHandlerBasicActions({
    sortFieldInitial: 'name',
    getAll: MutualsService.getAll,
    removeAction: MutualsService.remove,
    editAction: MutualsService.update,
    createAction: MutualsService.create,
  })

  const handleCreate = async (data) => {
    const resp = await dataFacade.create(data)
    if (resp) {
      toggleModal({})
    }
  }

  const handleUpdateMutual = async (data) => {
    const body = {
      name: data?.name,
      addresses: data?.addresses,
    }
    const resp = await dataFacade.edit({ _id: data?._id, body })
    if (resp) {
      toggleEditModal({})
    }
  }

  const toggleEditModal = (data) => {
    updateInputs(data)
    toggleEdit()
  }

  const toggleModal = () => {
    updateInputs({})
    toggle()
  }

  const buttonEdit = (rowData) => (
    <EditButton onClick={() => toggleEditModal(rowData)} />
  )

  const showMutualsAddress = (rowData) => {
    return rowData?.addresses?.map((address) => (
      <span className="address"> {address}</span>
    ))
  }

  const headerOptions = {
    title: msg({ id: 'mutuals' }),
    handlerHeaderActions: {
      showBackButton: true,
      secondaryButton: canAdd(MODULES.SETTINGS, GROUPS.MUTUAL) && toggle,
      secondaryButtonText: 'button.add',
      secondaryButtonIcon: 'pi-plus',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
  }

  const dataTable = (
    <DataTableComp
      loading={dataFacade?.loading}
      tableOptions={{
        className: 'table-without-menu',
        value: dataFacade?.data?.resp,
        dataKey: '_id',
        editMode: 'row',
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column field="name" header={msg({ id: 'name' })} sortable />,
        <Column
          field="addresses"
          header={msg({ id: 'addresses' })}
          body={(rowData) => showMutualsAddress(rowData)}
        />,
        canDelete(MODULES.SETTINGS, GROUPS.MUTUAL) && (
          <Column
            body={(rowData) =>
              DataTableFun.buttonDelete(rowData, dataFacade.remove)
            }
            exportable={false}
          />
        ),
        canEdit(MODULES.SETTINGS, GROUPS.MUTUAL) && (
          <Column body={buttonEdit} exportable={false} />
        ),
      ]}
    />
  )

  return (
    <div className="mutuals-container">
      <MainCommonView
        headerOptions={headerOptions}
        componentData={dataTable}
        pagerOptions={pagerOptions}
      />

      <Modal
        isShowing={isShowing}
        hide={() => toggleModal({})}
        width="90%"
        maxWidth="450px"
        title={msg({ id: 'mutual.add' })}
        context={<MutualForm submit={handleCreate} />}
      />

      <Modal
        isShowing={isShowingEdit}
        hide={() => toggleEditModal({})}
        width="90%"
        maxWidth="450px"
        title={msg({ id: 'mutual.edit' })}
        context={
          <MutualForm mutualId={inputs?._id} submit={handleUpdateMutual} />
        }
      />
    </div>
  )
}

export default Mutuals
